/**
 * 到達性・レピュテーション監視 `/deliverability`（A142 / R84 / A157）。
 *
 * タブは 5 つ:
 *   rbl         … RBL（ブラックリスト）掲載中の出口 IP
 *   dns         … 出口 IP の PTR / FCrDNS 検証結果
 *   warmup      … IP ウォームアップの進行と日次上限
 *   service-dns … サービス SPF などの「あるべき値」と実測の差分
 *   reputation  … Google Postmaster / SNDS / JMRP などへの登録状況（R48）
 *
 * **なぜ画面が要るか。** R0 §2.3 でエージェントの報告を受け取る側は実装したが、
 * 運営が見る場所がどこにも無かった。掲載に気付く契機が「顧客からの問い合わせ」
 * しか無い状態は、送信ドメインのレピュテーションを預かる事業として成り立たない。
 *
 * **API の応答は snake_case のまま返る**（`dns-checks` / `ip-warmup` /
 * `service-dns` は生の SQL 行をそのまま返す実装）。camelCase に読み替えると
 * 全列が undefined になるので、`types/api.ts` の DTO どおりに読む。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, ExternalLink, Flame, Globe2, RefreshCw, Radar, ShieldAlert } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DataTable,
  Input,
  PageHeader,
  StatCard,
  StatusBadge,
  Textarea,
  formatCount,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import {
  REPUTATION_SCOPE_LABEL,
  REPUTATION_STATUS_LABEL,
  REPUTATION_STATUS_TONE,
  WARMUP_STATUS_LABEL,
  WARMUP_STATUS_TONE,
  labelFrom,
  optionsFromLabels,
  toneFrom,
} from '../lib/ops-labels.js';
import { usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type {
  CountedList,
  DnsCheckRow,
  IpWarmupRow,
  RblListing,
  ReputationProgramEntry,
  ReputationProgramNodeEntry,
  ReputationProgramsResponse,
  ServiceDnsList,
} from '../types/api.js';

const routeApi = getRouteApi(RID.deliverability);

export function DeliverabilityPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  return (
    <>
      <PageHeader
        title="到達性"
        description="出口 IP のレピュテーション（RBL 掲載・逆引き・ウォームアップ）とサービス DNS の突合。"
      />

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'rbl', label: 'RBL 掲載', icon: ShieldAlert },
          { value: 'dns', label: 'DNS 検査', icon: Globe2 },
          { value: 'warmup', label: 'IP ウォームアップ', icon: Flame },
          { value: 'service-dns', label: 'サービス DNS', icon: Radar },
          { value: 'reputation', label: 'レピュテーション登録', icon: ClipboardCheck },
        ]}
      />

      {search.tab === 'rbl' ? <RblTab /> : null}
      {search.tab === 'dns' ? <DnsChecksTab /> : null}
      {search.tab === 'warmup' ? <WarmupTab /> : null}
      {search.tab === 'service-dns' ? <ServiceDnsTab /> : null}
      {search.tab === 'reputation' ? <ReputationTab /> : null}
    </>
  );
}

/* -------------------------------------------------------------- RBL 掲載 */

function RblTab(): React.ReactElement {
  const search = routeApi.useSearch();

  const query = useQuery({
    queryKey: qk.rblListings({ nodeId: search.nodeId ?? null }),
    queryFn: () =>
      api.get<CountedList<RblListing>>('/rbl-listings', { serverNodeId: search.nodeId }),
    refetchInterval: 60_000,
  });

  const items = query.data?.items ?? [];
  // 同じ IP が複数ゾーンに載ることがあるので、IP 単位でも数える
  // （「3 件」がノード 1 台のことなのか 3 台のことなのかで打ち手が変わる）。
  const distinctIps = new Set(items.map((r) => r.ip)).size;
  const distinctNodes = new Set(items.map((r) => r.serverNodeId)).size;

  const columns: Array<DataTableColumn<RblListing>> = [
    { id: 'ip', header: '出口 IP', cell: (r) => <code className="font-mono text-sm">{r.ip}</code> },
    {
      id: 'zone',
      header: '掲載先',
      cell: (r) => <span className="font-mono text-xs text-muted-foreground">{r.zone}</span>,
    },
    {
      id: 'node',
      header: 'ノード',
      hideBelow: 'md',
      cell: (r) => (
        <Link
          to="/nodes/$nodeId"
          params={{ nodeId: r.serverNodeId }}
          search={{ tab: 'overview', range: '24h' }}
          className="hover:text-primary"
        >
          {r.serverNodeId}
        </Link>
      ),
    },
    { id: 'status', header: '状態', cell: (r) => <StatusBadge status={r.status} size="sm" dot /> },
    { id: 'firstSeenAt', header: '検知', hideBelow: 'md', cell: (r) => formatDateTime(r.firstSeenAt) },
    {
      id: 'lastCheckedAt',
      header: '最終確認',
      hideBelow: 'lg',
      cell: (r) => formatRelative(r.lastCheckedAt),
    },
    {
      id: 'delist',
      header: '解除申請',
      align: 'right',
      width: 'w-32',
      cell: (r) =>
        r.delistingUrl ? (
          // 解除申請は各 RBL 事業者のサイトで行う。外部遷移なので必ず新規タブ +
          // `noreferrer`（運営コンソールの URL を相手に渡さない）。
          <Button asChild variant="outline" size="sm">
            <a href={r.delistingUrl} target="_blank" rel="noreferrer noopener">
              <ExternalLink aria-hidden />
              申請する
            </a>
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="掲載中の件数"
          value={formatCount(items.length)}
          icon={ShieldAlert}
          tone={items.length > 0 ? 'destructive' : 'default'}
        />
        <StatCard label="影響を受けている IP" value={formatCount(distinctIps)} />
        <StatCard label="影響を受けているノード" value={formatCount(distinctNodes)} />
      </div>

      {items.length > 0 ? (
        <Alert variant="destructive">
          <AlertTitle>ブラックリストに掲載されています</AlertTitle>
          <AlertDescription>
            掲載中は同じ IP を使う全契約者のメールが届かなくなります。解除申請と並行して、
            原因（乗っ取り・大量のバウンス・苦情）を「セキュリティ &gt; 送信レート」で確認してください。
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(r) => r.id}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="掲載" />}
      />
    </div>
  );
}

/* ------------------------------------------------------------- DNS 検査 */

function DnsChecksTab(): React.ReactElement {
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.nodeWrite);

  const query = useQuery({
    queryKey: qk.dnsChecks(),
    queryFn: () => api.get<CountedList<DnsCheckRow>>('/dns-checks'),
    refetchInterval: 60_000,
  });

  const verify = useMutation({
    mutationFn: (serverNodeId: string) => api.post(`/nodes/${serverNodeId}/verify-dns`, {}),
    // 202 で受け付けるだけ。結果が入るのは worker の実行後なので、
    // ここで invalidate しても即座には変わらない（数十秒後の定期再取得で反映）。
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.dnsChecks() }),
  });

  const items = query.data?.items ?? [];
  const failing = items.filter((r) => !r.fcrdns_ok || !r.hostname_matches);

  const columns: Array<DataTableColumn<DnsCheckRow>> = [
    { id: 'ip', header: '出口 IP', cell: (r) => <code className="font-mono text-sm">{r.ip}</code> },
    {
      id: 'hostname',
      header: 'ノード',
      cell: (r) => (
        <Link
          to="/nodes/$nodeId"
          params={{ nodeId: r.server_node_id }}
          search={{ tab: 'overview', range: '24h' }}
          className="hover:text-primary"
        >
          {r.hostname}
        </Link>
      ),
    },
    {
      id: 'ptr',
      header: '逆引き（PTR）',
      hideBelow: 'md',
      cell: (r) =>
        r.ptr_names && r.ptr_names.length > 0 ? (
          <span className="font-mono text-xs">{r.ptr_names.join(', ')}</span>
        ) : (
          <span className="text-xs text-destructive">未設定</span>
        ),
    },
    {
      id: 'fcrdns',
      header: 'FCrDNS',
      cell: (r) =>
        r.fcrdns_ok ? (
          <Badge variant="success" size="sm">
            一致
          </Badge>
        ) : (
          <Badge variant="destructive" size="sm">
            不一致
          </Badge>
        ),
    },
    {
      id: 'hostnameMatches',
      header: 'HELO 名',
      hideBelow: 'lg',
      cell: (r) =>
        r.hostname_matches ? (
          <Badge variant="success" size="sm">
            一致
          </Badge>
        ) : (
          <Badge variant="warning" size="sm">
            不一致
          </Badge>
        ),
    },
    {
      id: 'problems',
      header: '検出された問題',
      hideBelow: 'lg',
      cell: (r) =>
        r.problems && r.problems.length > 0 ? (
          <span className="text-xs text-muted-foreground">{r.problems.join(' / ')}</span>
        ) : (
          '—'
        ),
    },
    { id: 'checkedAt', header: '検査', hideBelow: 'md', cell: (r) => formatRelative(r.checked_at) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-28',
      cell: (r) =>
        canWrite ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => verify.mutate(r.server_node_id)}
            loading={verify.isPending && verify.variables === r.server_node_id}
          >
            <RefreshCw aria-hidden />
            再検証
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-5">
      {failing.length > 0 ? (
        <Alert variant="warning">
          <AlertTitle>逆引きが揃っていない出口 IP が {failing.length} 件あります</AlertTitle>
          <AlertDescription>
            FCrDNS（正引きと逆引きが一致すること）が通らない IP からの送信は、Gmail など主要な受信側で
            高い確率で拒否されます。PTR は VPS 事業者のコンソールで設定したあと「再検証」を押してください
            （定期実行は 6 時間おきです）。
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(r) => `${r.server_node_id}-${r.ip}`}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="検査結果" />}
      />
    </div>
  );
}

/* --------------------------------------------------- IP ウォームアップ */

function WarmupTab(): React.ReactElement {
  const query = useQuery({
    queryKey: qk.ipWarmup(),
    queryFn: () => api.get<CountedList<IpWarmupRow>>('/ip-warmup'),
    refetchInterval: 60_000,
  });

  const items = query.data?.items ?? [];

  const columns: Array<DataTableColumn<IpWarmupRow>> = [
    { id: 'ip', header: '出口 IP', cell: (r) => <code className="font-mono text-sm">{r.ip}</code> },
    {
      id: 'hostname',
      header: 'ノード',
      hideBelow: 'md',
      cell: (r) => (
        <Link
          to="/nodes/$nodeId"
          params={{ nodeId: r.server_node_id }}
          search={{ tab: 'overview', range: '24h' }}
          className="hover:text-primary"
        >
          {r.hostname}
        </Link>
      ),
    },
    {
      id: 'status',
      header: '状態',
      cell: (r) => (
        <StatusBadge
          status={r.status}
          size="sm"
          dot
          pulse={r.status === 'running'}
          tone={toneFrom(WARMUP_STATUS_TONE, r.status)}
          label={labelFrom(WARMUP_STATUS_LABEL, r.status)}
        />
      ),
    },
    { id: 'day', header: '経過日', align: 'right', cell: (r) => `${r.current_day} 日目` },
    {
      id: 'limit',
      header: '本日の上限',
      align: 'right',
      // **ノード全体に効く値**。メールボックス単位だと Day1 を 50 通に絞っても
      // メールボックスが 100 個あればノードからは 5,000 通/日出る（R0 §2.3）。
      cell: (r) => `${formatCount(r.current_daily_limit)} 通/日`,
    },
    { id: 'startedOn', header: '開始', hideBelow: 'lg', cell: (r) => r.started_on ?? '—' },
    {
      id: 'completedAt',
      header: '完了',
      hideBelow: 'lg',
      cell: (r) => (r.completed_at ? formatDateTime(r.completed_at) : '—'),
    },
  ];

  return (
    <div className="space-y-5">
      <Alert>
        <AlertTitle>上限はノード全体に効きます</AlertTitle>
        <AlertDescription>
          新しい出口 IP からいきなり大量に送ると、受信側は「素性の分からない送信元」として
          扱います。ここに出ている上限は<strong>そのノードの合計</strong>で、メールボックス単位ではありません。
        </AlertDescription>
      </Alert>

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(r) => r.id}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="ウォームアップ計画" />}
      />
    </div>
  );
}

/* --------------------------------------------------------- サービス DNS */

function ServiceDnsTab(): React.ReactElement {
  const query = useQuery({
    queryKey: qk.serviceDns(),
    queryFn: () => api.get<ServiceDnsList>('/service-dns'),
    refetchInterval: 120_000,
  });

  const items = query.data?.items ?? [];
  const mismatched = items.filter((r) => r.matches === false);

  const columns: Array<DataTableColumn<ServiceDnsRowView>> = [
    {
      id: 'name',
      header: 'レコード名',
      cell: (r) => (
        <div className="space-y-0.5">
          <code className="font-mono text-sm">{r.name}</code>
          <span className="block text-xs text-muted-foreground">
            {r.rr_type} / {labelOf(r.purpose)}
          </span>
        </div>
      ),
    },
    {
      id: 'expected',
      header: 'あるべき値',
      cell: (r) => (
        <code className="block max-w-md break-all font-mono text-xs">{r.expected_value}</code>
      ),
    },
    {
      id: 'observed',
      header: '実測値',
      hideBelow: 'md',
      cell: (r) =>
        r.observed_value ? (
          <code className="block max-w-md break-all font-mono text-xs text-muted-foreground">
            {r.observed_value}
          </code>
        ) : (
          <span className="text-xs text-muted-foreground">未取得</span>
        ),
    },
    {
      id: 'matches',
      header: '一致',
      width: 'w-24',
      cell: (r) =>
        r.matches === null ? (
          <Badge variant="muted" size="sm">
            未突合
          </Badge>
        ) : r.matches ? (
          <Badge variant="success" size="sm">
            一致
          </Badge>
        ) : (
          <Badge variant="destructive" size="sm">
            不一致
          </Badge>
        ),
    },
    {
      id: 'checkedAt',
      header: '突合',
      hideBelow: 'lg',
      cell: (r) => (r.checked_at ? formatRelative(r.checked_at) : '—'),
    },
  ];

  return (
    <div className="space-y-5">
      {query.data?.hint ? (
        <Alert>
          <AlertDescription>{query.data.hint}</AlertDescription>
        </Alert>
      ) : null}

      {mismatched.length > 0 ? (
        <Alert variant="destructive">
          <AlertTitle>公開されている DNS が期待値と違います（{mismatched.length} 件）</AlertTitle>
          <AlertDescription>
            サービス SPF は<strong>全契約者の SPF が参照している 1 レコード</strong>です。
            差分がある間は毎時 critical が上がり続けます。「あるべき値」をそのままゾーンへ反映してください
            （DNS の公開は運営者の手作業です）。
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(r) => `${r.name}-${r.rr_type}-${r.purpose}`}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="突合結果" />}
      />

      <p className="text-xs text-muted-foreground">
        <RefetchingDot active={query.isFetching && !query.isLoading} /> 突合は deliverability キューの
        <code className="font-mono">spf.publish</code> が 1 時間ごとに行います。
      </p>
    </div>
  );
}

/** `DataTable` の型引数に使うだけの別名（列定義を読みやすくする）。 */
type ServiceDnsRowView = ServiceDnsList['items'][number];

/* ------------------------------------------------- レピュテーション登録 */

/**
 * Google Postmaster Tools / Microsoft SNDS / JMRP / Yahoo! CFL / Spamhaus DQS への
 * **登録状況の台帳**（R48）。
 *
 * ここに出るのは指標そのものではなく「登録したかどうか」。どれも登録が人手で、
 * しかも登録した事実がどこにも残っていなかった（手順書に書いても担当が替われば
 * 消える）。とくに Spamhaus DQS は、鍵付きゾーンへ切り替えないと**ある日突然
 * すべての問い合わせが拒否され**、その結果 RBL 判定が全ノードで critical になる。
 *
 * **未登録のサービスも API が必ず 1 行で返す。** 行が無いことを「対象外」と
 * 誤解させないための設計なので、画面でも隠さず警告色で出す。
 *
 * ノード単位（SNDS / JMRP）は台数ぶん登録が要るので、カタログの行とは別に
 * `nodeItems` を出す。カタログ側の 1 行は「サービスとして着手したか」を表す。
 */
function ReputationTab(): React.ReactElement {
  const canWrite = usePermission(OPS_PERMISSION.nodeWrite);
  const [editing, setEditing] = React.useState<ReputationProgramEntry | null>(null);

  const query = useQuery({
    queryKey: qk.reputationPrograms(),
    queryFn: () => api.get<ReputationProgramsResponse>('/reputation-programs'),
  });

  const items = query.data?.items ?? [];
  const notRegistered = items.filter((r) => r.status === 'not_registered').length;
  const overdue = items.filter((r) => r.reviewOverdue).length;
  const reviewDays = query.data?.reviewIntervalDays ?? 90;

  const columns: Array<DataTableColumn<ReputationProgramEntry>> = [
    {
      id: 'program',
      header: 'サービス',
      cell: (r) => (
        <div className="min-w-0 space-y-0.5">
          <span className="block truncate font-medium">{r.label}</span>
          <span className="block text-xs text-muted-foreground">
            {labelFrom(REPUTATION_SCOPE_LABEL, r.scope)}
          </span>
        </div>
      ),
    },
    {
      id: 'status',
      header: '登録',
      width: 'w-32',
      cell: (r) => (
        <Badge variant={toneFrom(REPUTATION_STATUS_TONE, r.status)} size="sm">
          {labelFrom(REPUTATION_STATUS_LABEL, r.status)}
        </Badge>
      ),
    },
    {
      id: 'identifier',
      header: '識別子',
      hideBelow: 'md',
      cell: (r) =>
        r.identifier ? (
          <code className="font-mono text-xs">{r.identifier}</code>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      id: 'verifiedAt',
      header: '最終確認',
      hideBelow: 'md',
      cell: (r) => (r.verifiedAt ? formatRelative(r.verifiedAt) : '—'),
    },
    {
      id: 'nextReview',
      header: '再確認期限',
      cell: (r) =>
        r.nextReviewDueAt ? (
          <span className={r.reviewOverdue ? 'text-destructive' : undefined}>
            {formatDateTime(r.nextReviewDueAt)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      id: 'why',
      header: 'なぜ要るか',
      hideBelow: 'lg',
      cell: (r) => <span className="text-xs text-muted-foreground">{r.why}</span>,
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-28',
      cell: (r) =>
        canWrite ? (
          <Button variant="outline" size="sm" onClick={() => setEditing(r)}>
            記録する
          </Button>
        ) : null,
    },
  ];

  const nodeItems = query.data?.nodeItems ?? [];

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard
          label="未登録"
          value={formatCount(notRegistered)}
          unit="件"
          icon={ClipboardCheck}
          tone={notRegistered > 0 ? 'warning' : 'success'}
        />
        <StatCard
          label="再確認の期限切れ"
          value={formatCount(overdue)}
          unit="件"
          icon={RefreshCw}
          tone={overdue > 0 ? 'destructive' : 'default'}
        />
        <StatCard label="ノード単位の登録" value={formatCount(nodeItems.length)} unit="件" icon={Flame} />
      </div>

      <Alert>
        <AlertTitle>ここに残るのは「登録した事実」だけです</AlertTitle>
        <AlertDescription>
          外部サービスへの申請そのものは運営者の手作業で、この画面からは行えません。
          登録は<strong>消えることがある</strong>（アカウント失効・ドメイン検証の期限切れ）ため、
          登録済みにすると {reviewDays} 日後に再確認の期限が立ちます。期限を過ぎた行は赤で出ます。
        </AlertDescription>
      </Alert>

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(r) => r.program}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="レピュテーション登録" />}
      />

      {nodeItems.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-medium">ノード単位の登録</h3>
          <DataTable
            columns={nodeColumns}
            rows={nodeItems}
            rowKey={(r) => r.id}
            emptyTitle="ノード単位の登録はありません"
          />
        </div>
      ) : null}

      <p className="text-xs text-muted-foreground">
        <RefetchingDot active={query.isFetching && !query.isLoading} /> 対象サービスのカタログは
        shared 側が正で、画面からは増やせません。
      </p>

      <ReputationRecordDialog entry={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

/** ノード単位（SNDS / JMRP）の生の行。カタログには畳まれない。 */
const nodeColumns: Array<DataTableColumn<ReputationProgramNodeEntry>> = [
  { id: 'program', header: 'サービス', cell: (r) => r.program },
  {
    id: 'node',
    header: 'ノード',
    cell: (r) =>
      r.serverNodeId ? (
        <Link
          to="/nodes/$nodeId"
          params={{ nodeId: r.serverNodeId }}
          search={{ tab: 'overview', range: '24h' }}
          className="font-mono text-xs hover:text-primary"
        >
          {r.serverNodeId}
        </Link>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
  },
  {
    id: 'status',
    header: '登録',
    cell: (r) => (
      <Badge variant={toneFrom(REPUTATION_STATUS_TONE, r.status)} size="sm">
        {labelFrom(REPUTATION_STATUS_LABEL, r.status)}
      </Badge>
    ),
  },
  {
    id: 'identifier',
    header: '識別子',
    hideBelow: 'md',
    cell: (r) =>
      r.identifier ? (
        <code className="font-mono text-xs">{r.identifier}</code>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
  },
  {
    id: 'nextReview',
    header: '再確認期限',
    cell: (r) => (r.nextReviewDueAt ? formatDateTime(r.nextReviewDueAt) : '—'),
  },
];

/**
 * 登録状況の記録（`PUT /ops/reputation-programs`）。
 *
 * **body は `.strict()`。** program / serverNodeId / status / identifier / notes
 * 以外を送ると 400 になるので、画面が余計なキーを足さないこと。
 * `serverNodeId` はノード単位のサービスでだけ送る（サービス全体のものに
 * ノード ID を付けると、カタログ側の行とは別の行が生えて未登録のまま残る）。
 */
function ReputationRecordDialog({
  entry,
  onClose,
}: {
  entry: ReputationProgramEntry | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [status, setStatus] = React.useState('registered');
  const [identifier, setIdentifier] = React.useState('');
  const [serverNodeId, setServerNodeId] = React.useState('');
  const [notes, setNotes] = React.useState('');

  React.useEffect(() => {
    if (!entry) return;
    // 未登録の行を開いたときは「登録済み」を既定にする（いちばん多い操作）。
    setStatus(entry.status === 'not_registered' ? 'registered' : entry.status);
    setIdentifier(entry.identifier ?? '');
    setNotes(entry.notes);
    setServerNodeId('');
  }, [entry]);

  const save = useMutation({
    mutationFn: () =>
      api.put('/reputation-programs', {
        program: entry?.program ?? '',
        status,
        identifier: identifier.trim() === '' ? null : identifier.trim(),
        // ノード単位のサービスでノードを指定しなければ null（＝サービス全体の行）。
        serverNodeId: entry?.scope === 'node' && serverNodeId.trim() !== '' ? serverNodeId.trim() : null,
        notes,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.reputationPrograms() });
      onClose();
    },
  });

  return (
    <FormDialog
      open={entry !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={entry ? `${entry.label} の登録状況を記録` : '登録状況を記録'}
      description={entry?.why}
      submitLabel="記録する"
      pending={save.isPending}
      error={save.error}
      onSubmit={() => save.mutate()}
    >
      <Field label="登録状態" required>
        <NativeSelect
          value={status}
          onChange={setStatus}
          options={optionsFromLabels(REPUTATION_STATUS_LABEL)}
        />
      </Field>

      <Field
        label="識別子"
        hint="Postmaster Tools のドメイン、SNDS の IP レンジなど。後から誰が見ても同じものを開けるように残します。"
      >
        <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="example.jp" />
      </Field>

      {entry?.scope === 'node' ? (
        <Field
          label="対象ノード ID"
          hint="空にするとサービス全体の 1 行として記録します。SNDS / JMRP は IP ごとの登録なので、ノード ID を入れて 1 台ずつ残してください。"
        >
          <Input
            value={serverNodeId}
            onChange={(e) => setServerNodeId(e.target.value)}
            placeholder="（空ならサービス全体）"
          />
        </Field>
      ) : null}

      <Field label="メモ" hint="申請日・担当・却下された理由など。次に確認する人が読みます。">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </Field>

      <p className="text-xs text-muted-foreground">
        「登録済み」にすると再確認の期限が立ち、期限切れは運営通知にも上がります。
      </p>
    </FormDialog>
  );
}
